import { useEffect, useRef, useState } from "react";

const MINIMUM_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SINGLE_RESULT_AUTO_SELECT_MS = 2000;
const MAXIMUM_RESULTS = 5;

const PLACE_DETAIL_FIELDS = [
  "address_components",
  "geometry",
  "formatted_address",
];

const AUTOCOMPLETE_REQUEST = {
  componentRestrictions: { country: "ie" },
  types: ["address"],
};

/**
 * The address suggestions behind "Search Address".
 *
 * Only the Maps JavaScript API is used. The two paths that used to be tried first —
 * an `/api/places/*` proxy that does not exist in this app, and a direct call to
 * `maps.googleapis.com/maps/api/place/*` which this key may not use and which sends no
 * CORS headers — could never succeed, and between them they turned every failure into
 * an empty prediction list: the user typed and nothing happened.
 *
 * Now there is one path, and when it cannot be used the caller is told so rather than
 * being shown no suggestions.
 */
export const useAddressAutocomplete = ({ onAddressResolved, mapsScript }) => {
  const [addressSearch, setAddressSearch] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [failureReason, setFailureReason] = useState("");

  const searchTimeoutRef = useRef(null);
  const autoSelectTimeoutRef = useRef(null);
  const pendingQueryRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const reportUnavailable = (reason) => {
    console.error("Address suggestions unavailable:", reason);
    pendingQueryRef.current = null;
    setIsSearching(false);
    setResults([]);
    setFailureReason(String(reason));
  };

  const searchAddresses = (query) => {
    if (!query || query.trim().length < MINIMUM_QUERY_LENGTH) {
      setResults([]);

      return;
    }

    const placesApi = window.google?.maps?.places;

    /*
     * The script may still be on its way. The query is held rather than dropped, and
     * runs as soon as the API is ready — an unanswered search must never look like a
     * search with no matches.
     */
    if (!placesApi?.AutocompleteService) {
      if (mapsScript.hasFailed) {
        reportUnavailable("Google Maps script unavailable");

        return;
      }

      pendingQueryRef.current = query;
      setIsSearching(true);

      return;
    }

    setIsSearching(true);

    try {
      const service = new placesApi.AutocompleteService();

      service.getPlacePredictions(
        { ...AUTOCOMPLETE_REQUEST, input: query },
        (predictions, status) => {
          setIsSearching(false);

          if (status === placesApi.PlacesServiceStatus.OK && predictions) {
            setFailureReason("");
            setResults(predictions.slice(0, MAXIMUM_RESULTS));

            return;
          }

          // No matches is an answer, not a failure.
          if (status === placesApi.PlacesServiceStatus.ZERO_RESULTS) {
            setFailureReason("");
            setResults([]);

            return;
          }

          reportUnavailable(`Places autocomplete status: ${status}`);
        }
      );
    } catch (error) {
      reportUnavailable(error);
    }
  };

  const fetchPlaceDetails = (placeId) =>
    new Promise((resolve, reject) => {
      const placesApi = window.google?.maps?.places;

      if (!placesApi?.PlacesService) {
        reportUnavailable("Google Maps Places service unavailable");
        reject(new Error("Places service unavailable"));

        return;
      }

      const service = new placesApi.PlacesService(
        document.createElement("div")
      );

      service.getDetails(
        { placeId, fields: PLACE_DETAIL_FIELDS },
        (place, status) => {
          if (status === placesApi.PlacesServiceStatus.OK && place) {
            onAddressResolved(place);
            resolve(place);

            return;
          }

          reportUnavailable(`Place details status: ${status}`);
          reject(new Error(`Failed to get place details: ${status}`));
        }
      );
    });

  const selectAddress = async (selectedAddress) => {
    try {
      setIsSearching(true);

      if (selectedAddress.address_components && selectedAddress.geometry) {
        onAddressResolved(selectedAddress);
      } else if (selectedAddress.place_id) {
        await fetchPlaceDetails(selectedAddress.place_id);
      } else {
        reportUnavailable("Prediction carried neither components nor a place id");
      }

      setAddressSearch(selectedAddress.description);
      setResults([]);
    } catch (error) {
      console.error("Failed to process selected address:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setAddressSearch(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (autoSelectTimeoutRef.current) {
      clearTimeout(autoSelectTimeoutRef.current);
    }

    if (value.trim().length < MINIMUM_QUERY_LENGTH) {
      pendingQueryRef.current = null;
      setResults([]);
      setIsSearching(false);

      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddresses(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const focusFirstResult = () => {
    containerRef.current?.querySelector("[data-address-result]")?.focus();
  };

  const focusSearchInput = () => {
    inputRef.current?.focus();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && results.length > 0) {
      event.preventDefault();
      selectAddress(results[0]);
    } else if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      focusFirstResult();
    }
  };

  // A query typed before the script was ready runs the moment it becomes ready.
  useEffect(() => {
    if (!mapsScript.isReady) return;

    const pendingQuery = pendingQueryRef.current;

    if (!pendingQuery) return;

    pendingQueryRef.current = null;
    searchAddresses(pendingQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchAddresses stable, omit to avoid callback churn
  }, [mapsScript.isReady]);

  // A query held while the script was still loading has nowhere to go once it fails.
  useEffect(() => {
    if (mapsScript.hasFailed && pendingQueryRef.current) {
      reportUnavailable("Google Maps script unavailable");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reportUnavailable stable
  }, [mapsScript.hasFailed]);

  /*
   * A single prediction is almost always the address the user meant, so it is applied
   * on its own after a pause — long enough to keep typing past it.
   */
  useEffect(() => {
    if (autoSelectTimeoutRef.current) {
      clearTimeout(autoSelectTimeoutRef.current);
    }

    if (
      results.length === 1 &&
      addressSearch.trim().length >= MINIMUM_QUERY_LENGTH
    ) {
      autoSelectTimeoutRef.current = setTimeout(() => {
        if (results.length === 1) {
          selectAddress(results[0]);
        }
      }, SINGLE_RESULT_AUTO_SELECT_MS);
    }

    return () => {
      if (autoSelectTimeoutRef.current) {
        clearTimeout(autoSelectTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectAddress stable, omit to avoid callback churn
  }, [results, addressSearch]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setResults([]);
      }
    };

    document.addEventListener("click", closeOnOutsideClick);

    return () => {
      document.removeEventListener("click", closeOnOutsideClick);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (autoSelectTimeoutRef.current) {
        clearTimeout(autoSelectTimeoutRef.current);
      }
    };
  }, []);

  return {
    addressSearch,
    results,
    isSearching,
    isUnavailable: !!failureReason,
    containerRef,
    inputRef,
    handleSearchChange,
    handleSearchKeyDown,
    selectAddress,
    focusSearchInput,
  };
};
